import { Bloodwork, Menopause, Gender } from '.';
import { MathUtils } from '../utils';
import { Ethnicity } from './Ethnicity';
import { MenstrualPhase } from './MenstrualPhase';

export abstract class PanelRange {
  id?: number;
  attribute: string;
  gender?: string;
  menopause?: Menopause;
  trimester?: number;
  ethnicity?: Ethnicity;

  ageMin?: number;
  ageMax?: number;

  labMin: number;
  labMax: number;

  mean?: number;

  stdDev2?: number;
  stdDev?: number;

  optimalMin?: number;
  optimalMax?: number;

  alarmMin?: number;
  alarmMax?: number;

  precision?: number;

  mpvMin?: number;
  mpvMax?: number;

  elevationMin?: number;
  elevationMax?: number;

  static findRange(
    panelRanges: Array<PanelRange>,
    attribute: string,
    bloodwork: Bloodwork,
  ): PanelRange {
    // No panelRanges or no attribute then just return
    if (!panelRanges || panelRanges.length === 0 || !attribute) {
      return undefined;
    }

    // Menstrual phase attribute wont have any ranges
    if (attribute === 'menstrualPhase') return undefined;

    let ranges: Array<PanelRange> = [];
    const gender = bloodwork.client.gender
      ? bloodwork.client.gender.toString()
      : Gender.FEMALE.toString();
    const isFemale = gender === Gender.FEMALE;
    const age =
      bloodwork.clientAgeAtCollectionDate() >= 0
        ? bloodwork.clientAgeAtCollectionDate()
        : 0;
    const elevation = bloodwork.client.elevation || 0;
    const menopause = bloodwork.client.menopause || Menopause.PREMENOPAUSE;
    const isPremenopausal = menopause === Menopause.PREMENOPAUSE;
    const isPregnant = menopause === Menopause.PREGNANT;
    const menstrualPhase = bloodwork.menstrualPhase || MenstrualPhase.NONE;
    const trimester = bloodwork.client.trimester;
    const mpv = bloodwork.meanPlateletVolume;
    const ethnicity = bloodwork.client.ethnicity || Ethnicity.WHITE;

    // If a panel range has a trimester set then set menopause to pregnant
    // so we can easily filter out based on gender and memopause
    // This could have been done in the database but that
    // would require hundreds of updates.
    panelRanges.forEach(range => {
      if (range.trimester >= 0) {
        range.gender = Gender.FEMALE;
        range.menopause = Menopause.PREGNANT;
      }
    });

    // This is only needed to lookup the range for the placeholders values
    // In Bloodwork Analysis, the attribute will already be set to
    // attribute + menstrualPhase
    if (
      isFemale &&
      isPremenopausal &&
      PanelRange.shouldFilterByMenstrualPhase(attribute, menstrualPhase)
    ) {
      attribute = `${attribute}${menstrualPhase}`;
    }

    // Filter on attribute
    ranges = panelRanges.filter(range => {
      return range.attribute === attribute;
    });

    // If we only have one range that matches on attribute then return it
    if (ranges.length === 1) {
      return ranges[0];
    }

    const rangesForAttribute = [...ranges];
    // console.log('rangesForAttribute:',rangesForAttribute);

    // Filter on gender
    ranges = ranges.filter(range => {
      return !range.gender || range.gender === gender;
    });

    // If we only have one range that matches on gender then return it
    if (ranges.length === 1) {
      return ranges[0];
    }

    const rangesForAttributeAndGender = [...ranges];
    // console.log('rangesForAttributeAndGender:',rangesForAttributeAndGender);

    // Filter on age
    ranges = ranges.filter(range => {
      return range.isInAgeRange(age);
    });

    // If we only have one range that matches on age then return it
    if (ranges.length === 1) {
      return ranges[0];
    }

    // Filter on Ethnicity, off the ROWS rather than off a list of attribute
    // names.
    //
    // This used to run only for the six attributes named in a hand-maintained
    // shouldFilterByEthnicity() list. Give an ethnicity-specific row to any
    // marker NOT on that list and the filter is skipped, so that row and the
    // general one both survive every remaining filter — and two survivors is
    // how findRange spells "no range at all", for every client of every
    // practitioner. That is precisely what the two African American
    // triglyceridesHDLRatio rows did: adults of every ethnicity lost the
    // TG/HDL range entirely.
    //
    // Reading it off the rows keeps the list from having to be remembered.
    // Prefer a row written for this client's ethnicity; otherwise use the rows
    // that name no ethnicity. That is the same answer the list gave for all six
    // markers — no row anywhere names White, Asian or Other, so those clients
    // fall back exactly as before — and a marker that gains an ethnicity row
    // now works the day the row lands.
    const rangesForEthnicity = ranges.filter(
      range => range.ethnicity === ethnicity,
    );
    ranges = rangesForEthnicity.length
      ? rangesForEthnicity
      : ranges.filter(range => !range.ethnicity);
    const rangesForAttributeAndGenderAndEthnicity = [...ranges];

    // If we only have one range that matches on ethnicity then return it
    if (ranges.length === 1) {
      return ranges[0];
    }

    // Filter on menopause if gender is Female
    if (isFemale) {
      // Filter on menopause
      ranges = ranges.filter(range => {
        return range.isInMenopauseRange(menopause);
      });

      // If we only have one range that matches on menopause then return it
      if (ranges.length === 1) {
        return ranges[0];
      }

      // Filter by trimester
      if (isPregnant) {
        ranges = ranges.filter(range => {
          return range.isInTrimesterRange(trimester);
        });
        // If we only have one range that matches on trimester then return it
        if (ranges.length === 1) {
          return ranges[0];
        }
      }
      // console.log('ranges after filter by trimester:', trimester, attribute, ranges);
    }

    // console.log('ranges after filter by age:', age, attribute, ranges);

    // Filter on elevation
    ranges = ranges.filter(range => {
      return range.isInElevationRange(elevation);
    });

    // If we only have one range that matches on mpv then return it
    if (ranges.length === 1) {
      return ranges[0];
    }

    // Filter on mpv
    if (PanelRange.shouldFilterByMPV(attribute)) {
      ranges = ranges.filter(range => {
        return range.isInMPVRange(mpv);
      });
    }

    // If we only have one range that matches on mpv then return it
    if (ranges.length === 1) {
      return ranges[0];
    }

    console.log('no matching range for: ', attribute);
    console.log(
      'age:',
      age,
      ' gender:',
      gender,
      ' ethnicity:',
      ethnicity,
      ' trimester:',
      trimester,
      ' menopause:',
      menopause,
      ' menstrualPhase:',
      menstrualPhase,
    );
    console.error(JSON.stringify(ranges, null, 2));
    if (ranges.length === 0) {
      console.error(
        'ranges for attribute: ',
        attribute,
        JSON.stringify(rangesForAttribute, null, 2),
      );
      console.error(
        'ranges for attribute and gender: ',
        attribute,
        JSON.stringify(rangesForAttributeAndGender, null, 2),
      );
      console.error(
        'ranges for attribute and gender and Ethnicity: ',
        attribute,
        JSON.stringify(rangesForAttributeAndGenderAndEthnicity, null, 2),
      );
    }

    return undefined;
  }

  static isInRange(value: number, min: number, max: number): boolean {
    return value >= min && value <= max;
  }

  // Markers whose reference ranges depend on the menstrual phase. This is the
  // single source of truth for "which markers require a menstrual phase" — used
  // both for range lookup here and for save-time validation in bloodwork-entry.
  static readonly MENSTRUAL_PHASE_ATTRIBUTES = [
    'estroneE1',
    'estradiolE2',
    'estrogensTotal',
    'progesterone',
    'fsh',
    'lh',
    'hydroxyprogesterone17',
  ];

  static shouldFilterByMenstrualPhase(
    attribute: string,
    menstrualPhase: MenstrualPhase,
  ) {
    if (!menstrualPhase) return false; // In case we change the default

    return PanelRange.MENSTRUAL_PHASE_ATTRIBUTES.includes(attribute);
  }

  static shouldFilterByMPV(attribute: string) {
    return ['plateletCount'].includes(attribute);
  }

  // shouldFilterByEthnicity() used to live here, naming the six attributes that
  // had ethnicity-specific rows. It is gone rather than merely unused: a list
  // that has to be updated by hand whenever a row is added to the database is a
  // list that will eventually be forgotten, and forgetting it takes the range
  // away from every client rather than failing visibly. findRange now reads the
  // ethnicity off the rows themselves.

  init?(precision: number): PanelRange {
    this.precision = precision;

    this.labMin = MathUtils.round(this.labMin, precision);
    this.labMax = MathUtils.round(this.labMax, precision);

    this.stdDev2 = MathUtils.stdDev([this.labMin, this.labMax]);
    this.stdDev = this.oneStdDev();

    // After calculating stdDev, round both to 3
    this.stdDev2 = MathUtils.round(this.stdDev2, 3);
    this.stdDev = MathUtils.round(this.stdDev, 3);

    if (this.optimalMin == null) {
      this.optimalMin = MathUtils.round(this.labMin + this.stdDev, precision);
    }

    if (this.optimalMax == null) {
      this.optimalMax = MathUtils.round(this.labMax - this.stdDev, precision);
    }

    this.mean = MathUtils.round(
      MathUtils.avg([this.optimalMin, this.optimalMax]),
      precision,
    );

    if (this.alarmMin == null) {
      this.alarmMin = MathUtils.round(this.labMin - this.stdDev, precision);
      if (this.alarmMin < 0) {
        this.alarmMin = 0;
      }
    }

    if (this.alarmMax == null) {
      this.alarmMax = MathUtils.round(this.labMax + this.stdDev, precision);
    }

    return this;
  }

  oneStdDev?(): number {
    return this.stdDev2 / 2;
  }

  placeholder?(precision: number = 1): string {
    return `e.g. ${MathUtils.round(this.mean, precision)}`;
  }

  isInMenopauseRange?(menopause: Menopause): boolean {
    if (this.menopause == null) {
      return true;
    }
    return menopause === this.menopause;
  }

  isInTrimesterRange?(trimester: number): boolean {
    if (this.trimester == null && trimester == null) {
      return true;
    }
    return trimester === this.trimester;
  }

  isInAgeRange?(age: number): boolean {
    if (this.ageMin == null || this.ageMax == null) {
      return true;
    }
    return PanelRange.isInRange(age, this.ageMin, this.ageMax);
  }

  isInMPVRange?(mpv: number): boolean {
    if (mpv != null && this.mpvMin != null && this.mpvMax != null) {
      return PanelRange.isInRange(mpv, this.mpvMin, this.mpvMax);
    }

    if (mpv == null && this.mpvMin == null && this.mpvMax == null) {
      return true;
    }
    return false;
  }

  isInElevationRange?(elevation: number): boolean {
    if (this.elevationMin == null && this.elevationMax == null) {
      return true;
    }
    return PanelRange.isInRange(
      elevation,
      this.elevationMin,
      this.elevationMax,
    );
  }

  isOptimal?(value: number): boolean {
    return PanelRange.isInRange(value, this.optimalMin, this.optimalMax);
  }

  isLab?(value: number): boolean {
    return PanelRange.isInRange(value, this.labMin, this.labMax);
  }

  isLessThanOptimal?(value: number): boolean {
    return value < this.optimalMin;
  }

  isGreaterThanOptimal?(value: number): boolean {
    return value > this.optimalMax;
  }

  isLessThanLab?(value: number): boolean {
    return value < this.labMin;
  }

  isGreaterThanLab?(value: number): boolean {
    return value > this.labMax;
  }

  isAlarm?(value: number): boolean {
    return PanelRange.isInRange(value, this.alarmMin, this.alarmMax);
  }

  isLessThanAlarm?(value: number): boolean {
    return value < this.alarmMin;
  }

  isGreaterThanAlarm?(value: number): boolean {
    return value > this.alarmMax;
  }

  isLessThanMean?(value: number): boolean {
    return value < this.mean;
  }

  isLessThanAvgMin?(value: number): boolean {
    return value < this.avgMin();
  }

  isGreaterThanAvgMin?(value: number): boolean {
    return value > this.avgMin();
  }

  isGreaterThanOrEqualToAvgMin?(value: number): boolean {
    return value >= this.avgMin();
  }

  isGreaterThanAvgMax?(value: number): boolean {
    return value > this.avgMax();
  }

  isLessThanAvgMax?(value: number): boolean {
    return value < this.avgMax();
  }

  isLessThanOrIsAvgMax?(value: number): boolean {
    return value <= this.avgMax();
  }

  avgMin?(): number {
    if (this.optimalMin != null && this.labMin != null) {
      // (Opt Min + Lab Min) / 2
      return (this.optimalMin + this.labMin) / 2;
    } else {
      return 0;
    }
  }

  avgMax?(): number {
    if (this.optimalMax != null && this.labMax != null) {
      // (Opt Min + Lab Min) / 2
      return (this.optimalMax + this.labMax) / 2;
    } else {
      return 0;
    }
  }
}
